import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataGridPro,
  GridColDef,
  GridRowClassNameParams,
  GridRowId,
  GridRowSelectionModel,
  Toolbar,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { Typography } from '@mui/material';

import { SearchComponent } from '@/components/common/Search';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import SelectionToggle from './SelectionToggle';
import SelectionAndOrderController from './SelectionAndOrderController';
import OrderController from './OrderController';
import { SelectionType, SelectorTranslation } from './SelectorTypes';
import { Identifiable } from '@/types';
import { useNotificationStore } from '@/stores/notificationstore';
import { ctxUser } from '@/contexts/UserContext';
import { CountResponseDto } from '@/generated/model';

import './MultiElementSelector.css';

function norm(s: string) {
  return s.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

// FIXME change to builtin difference in the future
function setDifference<T>(a: Set<T>, b: Set<T>): Set<T> {
  return new Set(Array.from(a).filter((item) => !b.has(item)));
}

export default function MultiElementSelector<T extends Identifiable>({
  selectedElements,
  setSelectedElements,
  searchText,
  setSearchText,
  fetchData,
  getRowCount,
  maxElementNumber,
  columns,
  translation,
  rowClassProvider,
  searchFilter,
}: {
  selectedElements: T[] | undefined;
  setSelectedElements: Dispatch<SetStateAction<T[] | undefined>>;
  searchText: string | undefined;
  setSearchText: Dispatch<SetStateAction<string | undefined>>;
  fetchData: (
    page: number,
    pageSize: number,
    pagesToLoad: number,
    filter: string | undefined,
    order: string
  ) => Promise<T[]>;
  getRowCount: (filter: string | undefined, selectedIds: number[]) => Promise<CountResponseDto>;
  maxElementNumber: number;
  columns: GridColDef[];
  translation: SelectorTranslation;
  rowClassProvider?: (params: GridRowClassNameParams<T>) => string;
  searchFilter?: (param: T, searchText: string) => boolean;
}) {
  const { t } = useTranslation();
  const showNotification = useNotificationStore((state) => state.showNotification);
  const userCtx = useContext(ctxUser);
  const [rows, setRows] = useState<T[]>([]);
  const [page, setPage] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [rowCount, setRowCount] = useState<number>(-1);
  const [allRowCount, setAllRowCount] = useState<number | undefined>(undefined);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  // All ids that are in selected state in the table
  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(selectedElements ? selectedElements.map((e) => e.id) : []),
  });
  // Ids that are selected in the current view (like: search when the user search for something)
  const [currentlySelectedIds, setCurrentlySelectedIds] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(selectedElements ? selectedElements.map((e) => e.id) : []),
  });
  const [selectionState, setSelectionState] = useState<SelectionType>('all');
  const apiRef = useGridApiRef();
  const pageSize = 20;

  function strip(name: string) {
    return name.replace(/[^\p{Letter}\p{Number}]/gu, ''); // ami nem betu es nem szam azt nem hasznaljuk a rendezesnel
  }

  function compare(a: T, b: T) {
    if (!a && !b) return 0;
    if (a.name === undefined && b.name === undefined) return 0;
    if (a.name === undefined) return 1;
    if (b.name === undefined) return -1;

    const c = strip(b.name).localeCompare(strip(a.name));
    if (c !== 0) return c;

    if (!a.id && !b.id) return 0;
    if (a.id === undefined) return 1;
    if (b.id === undefined) return -1;
    // ez mindig igaz, csak a tipusdefinicio rossz
    if (typeof a.id === 'number' && typeof b.id === 'number') return b.id - a.id;
    return 0;
  }

  // parameters needed, because the set states in useEffect happens on different thread
  const loadRows = useCallback(
    async (pageNo: number, pagesToLoad: number, nowHasMore: boolean) => {
      if (loading || !nowHasMore) return;
      setLoading(true);

      const newRows = await fetchData(pageNo, pageSize, pagesToLoad, searchText, order);
      if (newRows.length < pageSize) setHasMore(false); // Stop loading if no more data
      setRows((prevRows) => [...prevRows, ...newRows]);
      if (apiRef.current) {
        apiRef.current.setRowSelectionModel(selectedIds);
      }
      setPage((prevPage) => prevPage + pagesToLoad); // Increase page count
      setLoading(false);
    },
    [fetchData, loading, order, searchText, selectedIds, apiRef]
  );
  function shownRows() {
    switch (selectionState) {
      case 'all':
        return rows;
      case 'selected': {
        let fitleredSelection = selectedElements ? selectedElements.filter((e) => selectedIds.ids.has(e.id)) : [];

        if (searchText) {
          if (!searchFilter) {
            fitleredSelection = fitleredSelection.filter(
              (f) =>
                (f.name && norm(f.name).indexOf(norm(searchText)) !== -1) ||
                (f.description && norm(f.description).indexOf(norm(searchText)) !== -1)
            );
          } else {
            fitleredSelection = fitleredSelection.filter((f) => searchFilter(f, searchText));
          }
        }

        if (order === 'asc') {
          fitleredSelection = fitleredSelection.sort(compare).reverse();
        } else {
          fitleredSelection = fitleredSelection.sort(compare);
        }

        return fitleredSelection;
      }
    }
  }

  const updateRowCount = useCallback(async () => {
    const localSelectedIds: number[] = Array.from(selectedIds.ids).map(Number); // FIXME nem kellene a values() ???
    const response = await getRowCount(searchText, localSelectedIds);
    setRowCount(response.count);
    setCurrentlySelectedIds({ type: 'include', ids: new Set(response.ids) });
  }, [getRowCount, searchText, selectedIds]);

  const updateAllRowCount = useCallback(async () => {
    const response = await getRowCount('', []);
    setAllRowCount(response.count);
  }, [getRowCount]);

  useEffect(() => {
    void updateAllRowCount();
  }, [updateAllRowCount]);

  useEffect(() => {
    if (!apiRef || !apiRef.current) return;
    if (selectionState == 'all' || selectionState == 'selected') {
      apiRef.current.setRowSelectionModel(selectedIds);
    }
    apiRef.current.scrollToIndexes({
      rowIndex: 0,
    });
  }, [selectionState]);

  useEffect(() => {
    const oldSelection = selectedElements ? selectedElements.filter((e) => selectedIds.ids.has(e.id)) : [];
    const newSelection = rows.filter((row) => selectedIds.ids.has(row.id));

    // Set won't deduplicate correctly because of different references
    const newElements = new Map([...oldSelection, ...newSelection].map((item) => [item.id, item])).values();
    setSelectedElements(Array.from(newElements));
  }, [selectedIds]);

  useEffect(() => {
    async function search() {
      await updateRowCount();
      setRows([]);
      setHasMore(true);
      setPage(0);
      await loadRows(0, 1, true);
    }
    void search();
  }, [searchText, order]);

  async function loadMoreRows() {
    await loadRows(page, 1, hasMore);
  }

  function handleSelectionChange(newSelection: GridRowSelectionModel) {
    const newSelectionArray = Array.from(newSelection.ids);
    const selectedIdsArray = Array.from(selectedIds.ids);
    const newRows = newSelectionArray.filter((id) => !selectedIdsArray.includes(id));
    const removedRows = selectedIdsArray.filter((id) => !newSelectionArray.includes(id));
    setSelectedIds((prev) => {
      const newSelected = Array.from(prev.ids).filter(
        (id) => !isIdInRows(shownRows(), id) || !removedRows.includes(id)
      );
      const updatedSelection = [...newSelected, ...newRows];
      if (updatedSelection.length > maxElementNumber!) {
        showTooManySelectedNotification();
        apiRef.current?.setRowSelectionModel(selectedIds);
        return prev;
      }
      setCurrentlySelectedIds((currentPrev) => {
        const currentNewSelection = Array.from(currentPrev.ids).filter(
          (id) => !isIdInRows(shownRows(), id) || !removedRows.includes(id)
        );
        return {
          type: 'include',
          ids: new Set([...currentNewSelection, ...newRows]),
        } satisfies GridRowSelectionModel;
      });
      return {
        type: 'include',
        ids: new Set([...updatedSelection]),
      } satisfies GridRowSelectionModel;
    });
  }

  function isIdInRows(rows: T[] | undefined, id: GridRowId): boolean {
    if (!rows) return false;
    const foundElement = rows.find((row) => row.id == id);
    return foundElement != undefined;
  }

  function showTooManySelectedNotification() {
    showNotification('info', t('error.api.too_many_report_target', { targetLimit: maxElementNumber }), undefined, 5);
  }

  async function selectMaxRows() {
    if (!maxElementNumber) return;
    const neededRows = maxElementNumber - rows.length;
    if (Math.min(neededRows, rowCount) > rows.length) {
      const neededPages = Math.ceil(neededRows / pageSize) + 1;
      await loadRows(page, neededPages, true);
    }
    setTimeout(() => selectFirstNRows(), 100);
  }

  async function selectFirstNRows() {
    const n = maxElementNumber - (selectedElements ? selectedElements.length : 0);
    const firstNIds = apiRef.current?.getAllRowIds().slice(0, n);
    apiRef.current?.setRowSelectionModel({ type: 'include', ids: new Set(firstNIds) });
  }

  function resetSelection() {
    const idsToKeep = setDifference(selectedIds.ids, currentlySelectedIds.ids); // .filter((id) => !currentlySelectedIds.ids.has(id));
    apiRef.current?.setRowSelectionModel({ type: 'include', ids: idsToKeep as Set<GridRowId> });
    setSelectedIds({ type: 'include', ids: idsToKeep as Set<GridRowId> });
    setSelectedElements((prev) => (prev ? prev.filter((p) => idsToKeep.has(p.id)) : prev));
    setCurrentlySelectedIds({ type: 'include', ids: new Set() });
  }

  function getColumnHeaders() {
    if (userCtx?.isToken()) {
      return {};
    }
    return {
      toolbar: () => (
        <Toolbar style={{ minHeight: '43px' }}>
          <div className="selector-header-container">
            <SelectionAndOrderController
              selectedRowIds={currentlySelectedIds}
              resetSelection={resetSelection}
              selectMaxRows={selectMaxRows}
              selectionState={selectionState}
              maxRowNum={maxElementNumber}
              totalRows={rowCount}
              orderController={<OrderController order={order} setOrder={setOrder} />}
            />
          </div>
        </Toolbar>
      ),
    };
  }

  function getStyle() {
    return {
      '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader': {
        display: 'none',
      },
      ...(userCtx?.isToken() && {
        '& .MuiDataGrid-virtualScroller': {
          marginTop: '0 !important',
        },
      }),
    };
  }

  return (
    <>
      {!isPhoneOrHighScreen() && (
        <Typography variant="h6" data-qa-element-id="multi_element_selector_title">
          {translation.header}
        </Typography>
      )}
      <SelectionToggle
        selectedCount={selectedIds.ids.size}
        allRowCount={allRowCount}
        selectionState={selectionState}
        setSelectionState={setSelectionState}
      />
      <div>
        <SearchComponent searchText={searchText} setSearchText={setSearchText} placeholder={translation.placeHolder} />
      </div>
      <DataGridPro
        showToolbar
        rows={shownRows()}
        columns={columns}
        disableRowSelectionOnClick
        pageSizeOptions={[pageSize]}
        paginationMode="server"
        onRowsScrollEnd={loadMoreRows}
        loading={loading}
        rowCount={rowCount}
        checkboxSelection
        onRowSelectionModelChange={handleSelectionChange}
        apiRef={apiRef}
        hideFooter
        sx={getStyle()}
        columnHeaderHeight={0}
        getRowClassName={rowClassProvider}
        localeText={{
          noRowsLabel: translation.noRows,
        }}
        slots={getColumnHeaders()}
      />
    </>
  );
}
